<template>
  <div class="relative">
    <!-- Main Content -->
    <div class="p-6">
          <!-- Control Section -->
          <div class="mb-6 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                <q-icon name="play_arrow" class="text-white text-xl" />
              </div>
              <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">订阅控制</h2>
            </div>

            <div class="flex gap-3">
              <button
                v-if="!isSubscribed"
                @click="startSubscribe"
                :disabled="isLoading"
                class="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
              >
                <div class="flex items-center justify-center gap-2">
                  <q-spinner-dots v-if="isLoading" class="text-white" />
                  <q-icon v-else name="notifications_active" />
                  <span>{{ isLoading ? "订阅中..." : "开始订阅" }}</span>
                </div>
              </button>

              <button
                v-else
                @click="stopSubscribe"
                class="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                <div class="flex items-center justify-center gap-2">
                  <q-icon name="stop" />
                  <span>停止订阅</span>
                </div>
              </button>

              <button
                @click="clearData"
                :disabled="dataList.length === 0"
                class="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
              >
                <div class="flex items-center justify-center gap-2">
                  <q-icon name="clear_all" />
                  <span>清空数据</span>
                </div>
              </button>
            </div>

            <!-- Status -->
            <div class="mt-4 flex items-center gap-3">
              <div
                :class="
                  isSubscribed
                    ? 'w-3 h-3 bg-green-500 rounded-full animate-pulse'
                    : 'w-3 h-3 bg-red-500 rounded-full'
                "
              ></div>
              <span
                :class="
                  isSubscribed
                    ? 'text-green-600 dark:text-green-400 font-semibold'
                    : 'text-red-600 dark:text-red-400 font-semibold'
                "
              >
                {{ isSubscribed ? "已订阅" : "未订阅" }}
              </span>
              <span class="text-gray-500 dark:text-gray-400 ml-4">
                已接收 {{ dataList.length }} 条数据
              </span>
            </div>
          </div>

          <!-- Publish Section -->
          <div class="mb-6 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                <q-icon name="send" class="text-white text-xl" />
              </div>
              <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">发布 SusPreviewInfo</h2>
            </div>

            <button
              @click="sendSusPreviewInfo"
              :disabled="isSending"
              class="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
            >
              <div class="flex items-center justify-center gap-2">
                <q-spinner-dots v-if="isSending" class="text-white" />
                <q-icon v-else name="send" />
                <span>{{ isSending ? "发送中..." : "发送 SusPreviewInfo 数据" }}</span>
              </div>
            </button>
          </div>

          <!-- Data Display Section -->
          <div class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <q-icon name="data_usage" class="text-white text-xl" />
              </div>
              <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">接收数据</h2>
            </div>

            <!-- Data List -->
            <div class="space-y-4 max-h-[600px] overflow-y-auto pr-2">
              <div
                v-for="(item, index) in dataList"
                :key="index"
                class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-4 border border-blue-200 dark:border-blue-700/50 hover:shadow-lg transition-all duration-300"
              >
                <!-- Header -->
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center gap-2">
                    <q-icon
                      :name="getTypeIcon(item.type)"
                      :class="getTypeColor(item.type)"
                      class="text-xl"
                    />
                    <span class="font-bold text-gray-800 dark:text-gray-200 text-lg">
                      {{ getTypeLabel(item.type) }}
                    </span>
                  </div>
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{ new Date(item.timestamp).toLocaleTimeString() }}
                  </span>
                </div>

                <!-- Data Content -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div
                    v-for="(value, key) in item.data"
                    :key="key"
                    class="bg-white dark:bg-gray-700/50 rounded-lg p-2 border border-gray-200 dark:border-gray-600"
                  >
                    <div class="text-xs text-gray-500 dark:text-gray-400 mb-1 font-semibold uppercase tracking-wide">
                      {{ key }}
                    </div>
                    <div class="text-sm font-mono font-bold text-gray-800 dark:text-gray-200">
                      {{ formatValue(value) }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div
                v-if="dataList.length === 0"
                class="text-center py-12 text-gray-500 dark:text-gray-400"
              >
                <q-icon name="inbox" size="64px" class="mb-4 opacity-50" />
                <p class="text-lg">暂无数据</p>
                <p class="text-sm mt-2">点击"开始订阅"按钮开始接收数据</p>
              </div>
            </div>
          </div>
        </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted } from "vue";
import { useQuasar } from "quasar";
import VBSSubscribeClient, { VBSData } from "@/utils/capacitor/vbsSubscribePlugin";
import VBSPublishClient from "@/utils/capacitor/vbsPublishPlugin";
import { isNative } from "@/utils/capacitor";

const $q = useQuasar();

const isSubscribed = ref(false);
const isLoading = ref(false);
const isSending = ref(false);
const dataList = ref<VBSData[]>([]);

// 开始订阅
const startSubscribe = async () => {
  if (!isNative) {
    $q.notify({
      type: "warning",
      message: "此功能仅在原生应用中可用",
      position: "top",
    });
    return;
  }

  isLoading.value = true;
  try {
    await VBSSubscribeClient.subscribe((data: VBSData) => {
      console.log("[VBS Subscribe] 收到数据:", data);
      // 将新数据添加到列表开头
      dataList.value.unshift(data);
      // 限制最多显示 10 条数据
      if (dataList.value.length > 10) {
        dataList.value = dataList.value.slice(0, 10);
      }
    });
    isSubscribed.value = true;
    $q.notify({
      type: "positive",
      message: "订阅成功，开始接收数据",
      position: "top",
    });
  } catch (error) {
    console.error("订阅失败:", error);
    $q.notify({
      type: "negative",
      message: "订阅失败: " + (error as Error).message,
      position: "top",
    });
  } finally {
    isLoading.value = false;
  }
};

// 停止订阅
const stopSubscribe = async () => {
  try {
    await VBSSubscribeClient.unsubscribe();
    isSubscribed.value = false;
    $q.notify({
      type: "positive",
      message: "已停止订阅",
      position: "top",
    });
  } catch (error) {
    console.error("停止订阅失败:", error);
    $q.notify({
      type: "negative",
      message: "停止订阅失败: " + (error as Error).message,
      position: "top",
    });
  }
};

// 清空数据
const clearData = () => {
  dataList.value = [];
  $q.notify({
    type: "info",
    message: "已清空数据",
    position: "top",
  });
};

// 发送 SusPreviewInfo 数据
const sendSusPreviewInfo = async () => {
  if (!isNative) {
    $q.notify({
      type: "warning",
      message: "此功能仅在原生应用中可用",
      position: "top",
    });
    return;
  }

  isSending.value = true;
  try {
    const sampleData: BumpyMap.SusPreviewInfoData = {
      MSG_PCM_FwdRoadType: 1,
      MSG_PCM_SusCtrlTimeStamp: Date.now(),
      MSG_PCM_FwdRoadFeat_FL: [1, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      MSG_PCM_SusObjHght_FL: [100.5, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      MSG_PCM_SusObjLengh_FL: [200.5, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      MSG_PCM_SusObjDist_FL: [300.5, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      MSG_PCM_GpsInfo_Longitude: 116.397128,
      MSG_PCM_GpsInfo_Latitude: 39.916527,
      MSG_PCM_GpsInfo_HeadingAngle: 0,
    };
    await VBSPublishClient.sendVBSData(sampleData);
    $q.notify({
      type: "positive",
      message: "SusPreviewInfo 数据已发送",
      position: "top",
    });
  } catch (error) {
    console.error("发送失败:", error);
    $q.notify({
      type: "negative",
      message: "发送失败: " + (error as Error).message,
      position: "top",
    });
  } finally {
    isSending.value = false;
  }
};

// 获取类型标签
const getTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    SusPreviewInfo: "预瞄信息",
    PositioningData: "定位数据",
    GNSSLOCInfo: "GNSS 位置信息",
    VehicleDriveState: "车辆驾驶状态",
  };
  return labels[type] || type;
};

// 获取类型图标
const getTypeIcon = (type: string): string => {
  const icons: Record<string, string> = {
    SusPreviewInfo: "visibility",
    PositioningData: "location_on",
    GNSSLOCInfo: "satellite",
    VehicleDriveState: "directions_car",
  };
  return icons[type] || "data_usage";
};

// 获取类型颜色
const getTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    SusPreviewInfo: "text-amber-500",
    PositioningData: "text-blue-500",
    GNSSLOCInfo: "text-green-500",
    VehicleDriveState: "text-purple-500",
  };
  return colors[type] || "text-gray-500";
};

// 格式化值
const formatValue = (value: any): string => {
  if (value === null || value === undefined) {
    return "--";
  }
  if (typeof value === "number") {
    // 如果是浮点数，保留 6 位小数
    if (value % 1 !== 0) {
      return value.toFixed(6);
    }
    return value.toString();
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return String(value);
};

// 组件卸载时清理
onUnmounted(async () => {
  if (isSubscribed.value) {
    try {
      await VBSSubscribeClient.unsubscribe();
    } catch (error) {
      console.error("组件卸载时停止订阅失败:", error);
    }
  }
});
</script>

<style scoped>
/* 自定义滚动条样式 */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

.dark .overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
